import React from "react";
import { AppState, KnightPosition } from "./Types";
import { findPossibleMoves, rollPosition } from "./Functions";
import { Grow, Typography } from "@mui/material";
import { Tile, alphabet } from "./Tile";

function Modal({
  message,
  message_2,
  success,
  onClick,
}: {
  message: string;
  message_2: string;
  success: boolean;
  onClick: any;
}) {
  return (
    <div
      className={"board-message " + (success ? "success" : "failure")}
      onClick={onClick}
    >
      <Typography
        variant="subtitle2"
        fontWeight={700}
        mt={1}
        fontSize={"1.2rem"}
      >
        {message}
      </Typography>
      <br />
      <Typography variant="caption" fontWeight={500} mb={1} fontSize={"0.9rem"}>
        {message_2}
      </Typography>
    </div>
  );
}

function Board({
  appState,
  setAppState,
}: {
  appState: AppState;
  setAppState: React.Dispatch<React.SetStateAction<AppState>>;
}) {
  const moveHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.dataset.row && event.currentTarget.dataset.col) {
      moveKnightToTile({
        row: parseInt(event.currentTarget.dataset.row),
        col: parseInt(event.currentTarget.dataset.col),
      });
    }
  };

  function resetBoard() {
    let newPos = rollPosition(appState.boardSize);
    setAppState({
      ...appState,
      pastRuns: [...appState.pastRuns, appState.pastMoves],
      knightPosition: newPos,
      pastMoves: [newPos],
      countdown: 3,
      finished: false,
    });
  }

  const moveKnightToTile = (target: KnightPosition) => {
    setAppState({
      ...appState,
      pastMoves: [...appState.pastMoves, target],
      knightPosition: target,
    });
  };

  let tiles = [];

  let possible = findPossibleMoves(appState);
  let possibleCoords: KnightPosition[];
  if (possible) {
    possibleCoords = possible?.map((move) => {
      return {
        row: move[0] + appState.knightPosition.row,
        col: move[1] + appState.knightPosition.col,
      };
    });
  } else {
    possibleCoords = [];
  }

  for (let row = appState.boardSize; row > 0; row--) {
    for (let col = 1; col <= appState.boardSize; col++) {
      const isActive =
        appState.knightPosition.row === row &&
        appState.knightPosition.col === col;
      let thisTilePosition: KnightPosition = {
        row: row,
        col: col,
      };
      const isTraversed = JSON.stringify(appState.pastMoves).includes(
        JSON.stringify(thisTilePosition)
      );
      const isPossible =
        JSON.stringify(possibleCoords).includes(
          JSON.stringify(thisTilePosition)
        ) && !appState.moving;
      let whichCorner = "none";

      if (row === 1 && col === 1) {
        whichCorner = "left-bottom";
      } else if (row === 1 && col === appState.boardSize) {
        whichCorner = "right-bottom";
      } else if (row === appState.boardSize && col === 1) {
        whichCorner = "left-top";
      } else if (row === appState.boardSize && col === appState.boardSize) {
        whichCorner = "right-top";
      }

      tiles.push(
        <Tile
          row={row}
          column={col}
          key={"tile-" + row + alphabet[col - 1]}
          boardSize={appState.boardSize}
          isWhite={(row + col) % 2 === 0 ? true : false}
          isTraversed={isTraversed}
          isActive={isActive}
          isPossible={isPossible}
          onClick={isPossible ? moveHandler : () => {}}
          corner={whichCorner}
        />
      );
    }
  }

  const BoardModal = () => {
    const success = appState.pastMoves.length === appState.boardSize ** 2;

    let msg2 = "";
    if (appState.countdown > 0 && appState.looping) {
      msg2 += "Restarting in " + appState.countdown;
    } else if (!appState.looping) {
      msg2 += "Click here to restart!";
    } else if (success) {
      msg2 += "Congratulations!";
    }

    return (
      <Modal
        message={success ? "Tour successful!" : "Out of moves!"}
        message_2={msg2}
        success={success}
        onClick={
          appState.finished && !appState.looping
            ? () => {
                resetBoard();
              }
            : () => {}
        }
      />
    );
  };

  return (
    <Grow
      in={true}
      style={{ transformOrigin: "0 0 0" }}
      {...(true ? { timeout: 400 } : {})}
    >
      <div className="board surface">
        {tiles}
        {appState.finished ? <BoardModal /> : ""}
      </div>
    </Grow>
  );
}

export default Board;
